// BottomActionButton.jsx
import React, { useEffect, useState, useRef } from 'react';

export default function BottomActionButton({ children, onClick, scrollThreshold = 200 }) {
  const [visible, setVisible] = useState(false);
  const [footerVisible, setFooterVisible] = useState(false);
  const btnRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    // Use visualViewport when available for more accurate mobile viewport height
    const getViewportY = () => (window.visualViewport ? window.visualViewport.pageTop || window.visualViewport.offsetTop || 0 : window.scrollY);
    const getViewportHeight = () => (window.visualViewport ? window.visualViewport.height : window.innerHeight);

    function check() {
      const doc = document.documentElement;
      const pageHeight = Math.max(doc.scrollHeight, document.body.scrollHeight);
      const scrolled = getViewportY();
      const should = scrolled > scrollThreshold && pageHeight > getViewportHeight() + 1;
      setVisible(should);
    }

    function onScrollThrottled() {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        check();
      });
    }

    check();
    window.addEventListener('scroll', onScrollThrottled, { passive: true });
    window.addEventListener('resize', onScrollThrottled);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onScrollThrottled);
      window.visualViewport.addEventListener('scroll', onScrollThrottled);
    }

    // MutationObserver to catch content changes that affect page height
    const mut = new MutationObserver(onScrollThrottled);
    mut.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('scroll', onScrollThrottled);
      window.removeEventListener('resize', onScrollThrottled);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onScrollThrottled);
        window.visualViewport.removeEventListener('scroll', onScrollThrottled);
      }
      mut.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [scrollThreshold]);

  useEffect(() => {
    // Observe footer visibility
    const footerEl = document.querySelector('#site-footer') || document.querySelector('footer') || document.querySelector('.footer');
    if (!footerEl) return undefined;

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => setFooterVisible(entry.isIntersecting));
    }, { root: null, threshold: 0.1 });

    io.observe(footerEl);
    return () => io.disconnect();
  }, []);

  // Hide when an input is focused (keyboard open) to avoid overlap with virtual keyboard
  useEffect(() => {
    function onFocus() {
      const active = document.activeElement;
      if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) {
        setVisible(false);
      }
    }

    window.addEventListener('focusin', onFocus);
    window.addEventListener('focusout', () => {
      // re-check on focus out (give a tick for layout changes)
      requestAnimationFrame(() => {
        const doc = document.documentElement;
        const pageHeight = Math.max(doc.scrollHeight, document.body.scrollHeight);
        if (pageHeight > window.innerHeight) setVisible(window.scrollY > scrollThreshold);
      });
    });

    return () => {
      window.removeEventListener('focusin', onFocus);
      window.removeEventListener('focusout', () => {});
    };
  }, [scrollThreshold]);

  // When visible, add bottom padding to body so the button doesn't cover content
  useEffect(() => {
    const padding = 88; // enough to accomodate the button + margin (adjust if needed)
    if (visible) {
      const prev = document.body.style.paddingBottom;
      document.body.style.paddingBottom = `${padding}px`;
      return () => { document.body.style.paddingBottom = prev; };
    }
    return undefined;
  }, [visible]);

  const shouldShow = visible && !footerVisible;

  return (
    <div className={`bottom-action ${shouldShow ? 'bottom-action--visible' : 'bottom-action--hidden'}`} aria-hidden={!shouldShow}>
      <button
        ref={btnRef}
        type="button"
        className="bottom-action__btn"
        onClick={onClick}
        aria-hidden={!shouldShow}
        aria-label={typeof children === 'string' ? children : 'Action'}
      >
        {children ?? 'En savoir plus'}
      </button>
    </div>
  );
}